// Temporary interfaces until autogenerated client is updated
// These match the backend ViewModels exactly

export interface CommentViewModel {
  id: string;
  pipeClassId: string;
  revisionId: number;
  contextType: string;
  parentCommentId?: string;
  authorName: string;
  authorId: string;
  content: string;
  createdDate: Date;
  updatedDate?: Date;
  displayOrder: number;
}

export interface SaveCommentsRequest {
  pipeClassId: string;
  revisionId: number;
  contextType: string;
  comments: CommentViewModel[];
}

export interface CreateCommentRequest {
  pipeClassId: string;
  revisionId: number;
  contextType: string;
  parentCommentId?: string;
  content: string;
  displayOrder: number;
}

export interface UpdateCommentRequest {
  id: string;
  content: string;
}

export interface CommentQueryParameters {
  pipeClassId?: string;
  revisionId?: number;
  contextType?: string;
}

// Import for type conversion
import { CommentViewModel as FrontendCommentViewModel, CommentResolutionStatus, CommentAuthorRole } from './comment.model';

// Convert backend CommentViewModel to frontend CommentViewModel.
// The backend now returns replies nested inside each comment, so we recurse.
export function autogeneratedToFrontendComment(
  backendComment: import('../../shared/models/autogenerated-piping').CommentViewModel,
  level: number = 0
): FrontendCommentViewModel {
  const nestedReplies: FrontendCommentViewModel[] = (backendComment.replies || [])
    .map(reply => autogeneratedToFrontendComment(reply, level + 1));

  return new FrontendCommentViewModel({
    id: backendComment.id || '',
    pipeClassId: backendComment.pipeClassId || '',
    revisionId: (backendComment.revisionId || 0).toString(),
    contextType: (backendComment.contextType || 'summary') as 'summary' | 'components' | 'valves',
    parentCommentId: backendComment.parentCommentId || null,
    authorName: backendComment.authorName || '',
    authorId: backendComment.authorId || '',
    // authorRole is nullable — null means the comment predates the role-tracking feature.
    authorRole: backendComment.authorRole != null ? (backendComment.authorRole as unknown as CommentAuthorRole) : null,
    content: backendComment.content || '',
    createdDate: backendComment.createdDate ? new Date(backendComment.createdDate) : new Date(),
    updatedDate: backendComment.updatedDate ? new Date(backendComment.updatedDate) : null,
    isEdited: !!backendComment.updatedDate,
    replies: nestedReplies,
    level,
    resolutionStatus: (backendComment.resolutionStatus as unknown as CommentResolutionStatus) ?? CommentResolutionStatus.Open
  });
}

// Convert backend CommentViewModel to frontend CommentViewModel (legacy function for manual types)
export function backendToFrontendComment(backendComment: CommentViewModel): FrontendCommentViewModel {
  return new FrontendCommentViewModel({
    id: backendComment.id,
    pipeClassId: backendComment.pipeClassId,
    revisionId: backendComment.revisionId.toString(),
    contextType: backendComment.contextType as 'summary' | 'components' | 'valves',
    parentCommentId: backendComment.parentCommentId || null,
    authorName: backendComment.authorName,
    authorId: backendComment.authorId,
    authorRole: null, // legacy path — no authorRole available
    content: backendComment.content,
    createdDate: new Date(backendComment.createdDate),
    updatedDate: backendComment.updatedDate ? new Date(backendComment.updatedDate) : null,
    isEdited: !!backendComment.updatedDate,
    replies: [], // Will be populated in frontend logic
    level: 0, // Will be calculated in frontend logic
    resolutionStatus: CommentResolutionStatus.Open // new comments always start as Open
  });
}

// Convert frontend CommentViewModel to backend CommentViewModel
export function frontendToBackendComment(frontendComment: FrontendCommentViewModel, displayOrder: number): CommentViewModel {
  return {
    id: frontendComment.id,
    pipeClassId: frontendComment.pipeClassId,
    revisionId: parseInt(frontendComment.revisionId),
    contextType: frontendComment.contextType,
    parentCommentId: frontendComment.parentCommentId,
    authorName: frontendComment.authorName,
    authorId: frontendComment.authorId,
    content: frontendComment.content,
    createdDate: frontendComment.createdDate,
    updatedDate: frontendComment.updatedDate,
    displayOrder: displayOrder
  };
}
